import { ListItem, ListItemIcon, ListItemText, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { differenceInMinutes, parseISO } from "date-fns";

import { TimeRangeLabel } from "../../../DateTime/TimeRangeLabel";
import { formatDayOfMonthWithDayName } from "../../../utils/formatDayOfMonthWithDayName";
import { formatHoursDurationCompact } from "../../../utils/formatHoursDurationCompact";
import { type TimeSlot } from "../../Open/types";
import { iconTypeByTimeSlotFilled } from "../../TimeSlotIcon";

interface ShiftBundleShiftInfoListItemProps {
  timeSlot: TimeSlot;
  startIso: string;
  endIso: string;
  payInMinorUnits: number | undefined;
  workplaceTimezone?: string;
}

export function ShiftBundleShiftInfoListItem(props: ShiftBundleShiftInfoListItemProps) {
  const { timeSlot, startIso, endIso, payInMinorUnits, workplaceTimezone } = props;

  const startDate = parseISO(startIso);
  const endDate = parseISO(endIso);
  const formattedDate = formatDayOfMonthWithDayName(startDate, { timezone: workplaceTimezone });
  const durationInHours = differenceInMinutes(endDate, startDate) / 60;
  const duration = formatHoursDurationCompact(durationInHours, { omitZeroMinutes: true });

  return (
    <ListItem>
      <ListItemIcon iconType={iconTypeByTimeSlotFilled[timeSlot]} />
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="body2">
              <Text inline semibold variant="body2">
                {formattedDate}
              </Text>
              {` \u00B7 ${duration}`}
            </Text>
            {isDefined(payInMinorUnits) && (
              <Text semibold variant="body2">
                {formatCentsAsUsd(payInMinorUnits)}
              </Text>
            )}
          </Stack>
        }
        secondary={
          <TimeRangeLabel
            semibold
            variant="body1"
            dateRange={{ startDate, endDate }}
            timezone={workplaceTimezone}
          />
        }
      />
    </ListItem>
  );
}
